import { PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { call, fork, put, select } from 'redux-saga/effects';
import {
  ChatMessageUploadPreparedPayload,
  NewMessagePayload,
  PendingChatImage,
} from 'features/chat/types';
import { SignalingSocket } from 'services/signaling';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { encryptE2eeMessageSaga } from 'features/chat/sagas/encryptE2eeMessageSaga';
import { replyCancelled, selectPendingChatImages } from 'features/chat/chatSlice';
import { uploadChatImagesSaga } from 'features/chat/sagas/uploadChatImagesSaga';
import { addOptimisticChatMessageSaga } from 'features/chat/sagas/addOptimisticChatMessageSaga';
import { clearPendingImagesSaga } from 'features/chat/sagas/clearPendingImagesOnChatChangeSaga';
import { notification } from 'features/notifications/toast/notification';
import i18n from 'i18n';

export function* sendMessageSaga(action: PayloadAction<NewMessagePayload>) {
  let { message } = action.payload;

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  if (e2eeEnabled) {
    message = yield call(encryptE2eeMessageSaga, action.payload.message);
  }

  const pendingImages: PendingChatImage[] = yield select(selectPendingChatImages);

  if (!message && pendingImages.length === 0) {
    return;
  }

  if (pendingImages.length > 0) {
    const images = pendingImages.map((p) => ({ name: p.name, fileSize: p.fileSize }));

    let response: ChatMessageUploadPreparedPayload;
    try {
      response = yield call(SignalingSocket.sendAsync, {
        event: 'sendChatMessage',
        data: {
          ...action.payload,
          message,
          images,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      yield call(clearPendingImagesSaga);
      yield call(notification, i18n.t('chat:image_upload.upload_failed'));
      return;
    }

    if (response.images?.length !== pendingImages.length) {
      Sentry.captureMessage(
        `Chat image upload: pending/prepared length mismatch (${pendingImages.length} vs ${response.images?.length ?? 0})`
      );
      yield call(clearPendingImagesSaga);
      yield call(notification, i18n.t('chat:image_upload.upload_failed'));
      return;
    }

    yield call(addOptimisticChatMessageSaga, {
      pendingImages,
      messageId: response.messageId,
      message: action.payload.message,
      preparedImages: response.images,
      targetUserId: action.payload.targetUserId,
      privateGroup: action.payload.privateGroup,
      parentId: action.payload.parentId,
    });

    yield fork(uploadChatImagesSaga, {
      messageId: response.messageId,
      pendingImages,
      preparedImages: response.images,
    });

    yield put(replyCancelled());

    return;
  }

  yield call(SignalingSocket.send, {
    event: 'sendChatMessage',
    data: {
      ...action.payload,
      message,
    },
  });

  yield put(replyCancelled());
}
